import { Container } from '@cleartrip/ct-design-components'

import { wrapper } from '@/redux/store/configureStore'

import Footer from '@/components/common/Footer'
import HomeHeader from '@/components/home/HomeHeader'
import SrpContainer from '@/containers/srp'
import { SRP_REVAMP_SURFACE_BG } from '@/constants/Srp'
import useSrpRevampExperiment from '@/hooks/srp/useSrpRevampExperiment'
import FooterMock from '@/mocks/footer.json'
import { getHeadersForSSR } from '@/utils'
import { isUserSignedInServerSide } from '@/utils/general/user'
import { useState } from 'react'
import { useRef } from 'react'
import { Collapse } from '@/components/Collapse/Collapse'
import { fetchSrpCoupons } from '@/api/srp'
import { getHostnameFromHeaders } from '@/utils/general/url'
import { getInternalServerDns } from '@server/utils/internalDns'

type SrpPageProps = {
  isSignedIn: boolean
  query: Record<string, string>
}
const SrpPage: React.FC<SrpPageProps> = ({ isSignedIn, query, srpCouponList = [] }) => {
  const { isSrpRevamp } = useSrpRevampExperiment()
  const initialTS = new Date()?.getTime()
  const returnDate = query?.return_date
  const fareType = 'FF'
  const pageScrollRef = useRef<HTMLDivElement>(null)
  const [headerCollapseState, setHeaderCollapseState] = useState<'open' | 'collapsed'>('open')
  const handleCollapseStateChange = (state: 'open' | 'collapsed') => {
    setHeaderCollapseState(state)
  }
  // Results can be shorter than the shell (loader, no flights); paint the leftover surface grey, not white.
  const pageShellStyle = isSrpRevamp && returnDate ? { backgroundColor: SRP_REVAMP_SURFACE_BG } : undefined
  // Legacy RT clips the shell; revamp RT lets it scroll so a leg that runs out hands the wheel on to the page.
  const rtOverflowClassName = isSrpRevamp ? 'oy-scroll no-scrollbar' : 'o-hidden'
  const pageShellClassName = `h-100vh w-100vh ${returnDate ? rtOverflowClassName : 'oy-scroll'}`
  return (
    <Container ref={pageScrollRef} className={pageShellClassName} style={pageShellStyle}>
      {/* The revamp scrolls the shell past the header instead, so skip the mount animation from height 0. */}
      <Collapse pose={headerCollapseState} initial={isSrpRevamp ? 'open' : undefined}>
      <header className='bc-line-500 p-relative bg-white flex flex-middle h-18 z-40'>
        <HomeHeader isSignedIn={isSignedIn} isSrp={true} isSrpRevamp={isSrpRevamp} />
      </header>
      </Collapse>
      <main style={isSrpRevamp ? { backgroundColor: SRP_REVAMP_SURFACE_BG } : undefined}>
        <SrpContainer 
          fareType={fareType} 
          initialTS={initialTS} 
          srpCouponList={srpCouponList} 
          pageScrollRef={pageScrollRef} 
          onCollapseSetHeaderState={handleCollapseStateChange}
          isSrpRevamp={isSrpRevamp}
        />
      </main>
      {/* @ts-expect-error */}
      <Footer data={{ data: FooterMock.slotData.data }} />
    </Container>
  )
}

export const getServerSideProps = wrapper.getServerSideProps(() => async context => {
  try {
    const { req, query } = context
    const cookies = req.cookies
    const signedIn = isUserSignedInServerSide(cookies)
    let srpCouponList
    const hostname = getHostnameFromHeaders(req.headers)
    const baseUrl = getInternalServerDns('oneDemand', req.url, hostname)
    const response = await fetchSrpCoupons({
        international: true,
        ssr: true,
        arrivalDate: query?.return_date,
        trafficSource: query?.traffic_source || '',
        baseUrl
      })
    srpCouponList = Array.isArray(response) ? response : []

    //Add meta cookies to response.
    await getHeadersForSSR(context, {
      addUtmSourceInCookie: true,
    })

    return {
      props: {
        isSignedIn: signedIn,
        query,
        srpCouponList,
      },
    }
  } catch (ex) {
    return {
      props: {
        isSignedIn: false,
        query: {},
        srpCouponList: [],
      },
    }
  }
})

export default SrpPage
